import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Departure countries with no LOCAL VAT number on file.
 *
 * v0.6.12 A2-184 — REDESIGNED from a filled amber alert to a quiet
 * note, on the operator's "it is plain confusing / too noisy" after
 * A2-183. Two reasons, and the second is the important one.
 *
 * 1. THE SCREEN HAD ONLY ONE VOLUME LEVEL. RegistrationsWarning, this,
 *    and FirstRunRenameBanner were all the same amber block, stacked.
 *    When everything is amber nothing reads as more important than
 *    anything else. Amber is now reserved for things that BLOCK OR
 *    CHANGE A FILING; this is a quiet note because of (2).
 *
 * 2. IT RUNS BEFORE THE CALC, SO IT CANNOT KNOW WHAT IT WAS CLAIMING.
 *    All it sees is the spreadsheet. It does not know whether any VAT
 *    is actually due in the departure country — the rows may all be
 *    reverse-charged, zero-rated or OSS-routed. The old copy said
 *    "You may have a VAT registration obligation in these countries",
 *    a legal conclusion reachable only AFTER the calc. That is
 *    registration_coverage's job, and the coverage panel is where the
 *    loud version belongs.
 *
 * The row count is what makes it actionable: before it existed, one
 * stray row and an entire file read identically.
 *
 * `detail` is [{country, rows, of_rows}] from GET /runs/ingest.
 * `countries` is the older plain-code list, kept so the component
 * still renders if only that is passed.
 */
export const UnregisteredDepartureBanner = ({ countries, detail }) => {
  const { t } = useTranslation();
  const rows = (detail && detail.length)
    ? detail
    : (countries || []).map((c) => ({ country: c }));
  if (!rows.length) return null;

  return (
    <div
      data-testid="calc-unregistered-departure-banner"
      className="border-t border-slate-200 dark:border-slate-700 px-4 py-2.5 flex items-start gap-2"
    >
      <span
        aria-hidden="true"
        className="text-slate-400 dark:text-slate-500 text-[13px] leading-5 shrink-0"
      >
        ⓘ
      </span>
      <p className="flex-1 min-w-0 text-[12px] leading-5 text-slate-600 dark:text-slate-300">
        {rows.map((r, i) => (
          <React.Fragment key={r.country}>
            {i > 0 && "; "}
            {t("ui.calculate.unregistered_departure.no_vat", { defaultValue: "No VAT number on file for" })}{" "}
            <span className="font-semibold text-slate-800 dark:text-slate-100">
              {r.country}
            </span>
            {Number.isFinite(r.rows) && Number.isFinite(r.of_rows) && r.of_rows > 0 && (
              <>
                {" — "}
                <span className="tabular-nums">
                  {t("ui.calculate.unregistered_departure.rows_of", { rows: r.rows, of: r.of_rows, defaultValue: `${r.rows} of ${r.of_rows}` })}
                </span>
                {t("ui.calculate.unregistered_departure.rows_sold", { defaultValue: " rows are sold from there" })}
              </>
            )}
          </React.Fragment>
        ))}
        {". "}
        <Link
          to="/admin/clients"
          data-testid="calc-unregistered-departure-banner-settings"
          className="underline decoration-dotted hover:text-slate-900 dark:hover:text-white"
        >
          {t("ui.calculate.unregistered_departure.check", { defaultValue: "Check registrations" })}
        </Link>
      </p>
    </div>
  );
};

export default UnregisteredDepartureBanner;
